/**
 * Sequence progress & content delivery — sequence engine progress, repeat
 * list, grow targeting cache, and the `enough_refresh_user_content` callable.
 *
 * Internal module — consumers import from the `userContentService` facade.
 */
import {
    doc,
    onSnapshot,
    Unsubscribe
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import {
    GrowTargetingCache,
    UserSequenceProgress,
    UserRepeatList,
} from '../../types';
import { USER_CONTENT_COLLECTION } from './shared';

export const subscribeToSequenceProgress = (
    uid: string,
    callback: (progress: UserSequenceProgress | null) => void
): Unsubscribe => {
    const progressRef = doc(db, USER_CONTENT_COLLECTION, uid, 'meta', 'sequenceProgress');
    return onSnapshot(progressRef, (snapshot) => {
        if (snapshot.exists()) {
            callback(snapshot.data() as UserSequenceProgress);
        } else {
            callback(null);
        }
    }, (error) => {
        console.error('Error subscribing to sequence progress:', error);
        callback(null);
    });
};

export const subscribeToRepeatList = (
    uid: string,
    callback: (repeatList: UserRepeatList | null) => void
): Unsubscribe => {
    const repeatListRef = doc(db, USER_CONTENT_COLLECTION, uid, 'meta', 'repeatList');
    return onSnapshot(repeatListRef, (snapshot) => {
        if (snapshot.exists()) {
            callback(snapshot.data() as UserRepeatList);
        } else {
            callback(null);
        }
    }, (error) => {
        console.error('Error subscribing to repeat list:', error);
        callback(null);
    });
};

/** Subscribe to grow targeting cache (eligible video/exercise IDs, computed once per UTC day) */
export const subscribeToGrowTargetingCache = (
    uid: string,
    callback: (cache: GrowTargetingCache | null) => void
): Unsubscribe => {
    const userContentRef = doc(db, USER_CONTENT_COLLECTION, uid);
    return onSnapshot(userContentRef, (snapshot) => {
        if (!snapshot.exists()) {
            callback(null);
            return;
        }
        const data = snapshot.data();
        const cache = data?.growTargetingCache;
        if (cache && cache.computedAt) {
            callback({
                eligibleVideoIds: cache.eligibleVideoIds || [],
                eligibleExerciseIds: cache.eligibleExerciseIds || [],
                computedAt: cache.computedAt,
            });
        } else {
            callback(null);
        }
    }, (error) => {
        console.error('Error subscribing to grow targeting cache:', error);
        callback(null);
    });
};

export interface RefreshUserContentOptions {
    focusLimit?: number;
    videoLimit?: number;
    exerciseLimit?: number;
    contentTypes?: string[];
    rerank?: boolean;
    seedDefaultsIfEmpty?: boolean;
    /** Force recompute grow targeting even if already set today. */
    forceGrowRetarget?: boolean;
    /** User's local date (YYYY-MM-DD) so the Cloud Function uses the correct day. */
    localDate?: string;
    /** Client platform: 'web' | 'ios' | 'android' */
    platform?: string;
}

export const refreshUserContent = async (options?: RefreshUserContentOptions): Promise<void> => {
    const refreshFn = httpsCallable(functions, 'enough_refresh_user_content');
    await refreshFn(options || {});
};
